import { createChart } from '@/analysis/helpers'
import { 
  mergeChartOptions, 
  chartTooltip, 
  percentageTooltipCallback 
} from '@/analysis/utils/chart-formatters'
import { calculateAllPnlMetrics } from '@/analysis/utils/pnl'
import { createMaybeAlsoStoreFn } from '@/analysis/utils/store-helpers'
import { formatDecimalBasedPercentage, formatCurrency, shortenCurrency } from '@/utils/string'

export function generatePnlOverviewChart(
  reports: any[], 
  canvasId: string, 
  config?: { alsoStore?: any }
) {
  const labels = reports.map((r) => {
    const d = new Date(r.period + '-01')
    return d.toLocaleString('default', { month: 'short', year: '2-digit' })
  })

  const revenueData = reports.map((r) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    return metrics['Pendapatan (Revenue)'] || 0
  })

  const expenseData = reports.map((r) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    const totalRevenue = metrics['Pendapatan (Revenue)'] || 0
    const netIncome = metrics['Pendapatan Bersih (Net Income)'] || 0
    return totalRevenue - netIncome
  })

  const profitData = reports.map((r) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    return metrics['Pendapatan Bersih (Net Income)'] || 0
  })

  if (config?.alsoStore) {
    const aiData: any = { revenue: {}, expense: {}, profit: {} }
    labels.forEach((label, i) => {
      aiData.revenue[label] = formatCurrency(revenueData[i])
      aiData.expense[label] = formatCurrency(expenseData[i])
      aiData.profit[label] = formatCurrency(profitData[i])
    })
    config.alsoStore(aiData, (v: any) => ({ pnl_overview: v }))
  }

  createChart(canvasId, 'bar', {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Revenue',
        data: revenueData,
        backgroundColor: '#3B82F6',
        order: 2,
      },
      {
        type: 'bar',
        label: 'Expense',
        data: expenseData,
        backgroundColor: '#EF4444',
        order: 3,
      },
      {
        type: 'line',
        label: 'Net Profit',
        data: profitData,
        borderColor: '#10B981',
        backgroundColor: '#10B981',
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 4,
        order: 1,
      },
    ],
  }, mergeChartOptions(
    {
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: (v: any) => shortenCurrency(v) }
        }
      }
    },
    chartTooltip({ label: (ctx: any) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` })
  ))
}

export function generateFinancialRatioChart(
  reports: any[], 
  config: { 
    canvasId: string
    metric: string
    title: string
    alsoStore?: any 
  }
) {
  const labels = reports.map((r) => {
    const d = new Date(r.period + '-01')
    return d.toLocaleString('default', { month: 'short', year: '2-digit' })
  })

  // [FIX] Calculate absolute values (Bar) and percentages (Line)
  const barData = reports.map((r) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    return metrics[config.metric] || 0
  })

  const lineData = reports.map((r) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    const revenue = metrics['Pendapatan (Revenue)'] || 0
    const absoluteValue = metrics[config.metric] || 0
    // Ensure this is a number
    return revenue > 0 ? (absoluteValue / revenue) * 100 : 0
  })

  if (config.alsoStore) {
    const aiData: Record<string, string> = {}
    labels.forEach((label, i) => {
      aiData[label] = `${formatDecimalBasedPercentage(lineData[i] / 100)} (${formatCurrency(barData[i])})`
    })
    config.alsoStore(aiData, (v: any) => ({ [`ratio_${config.title.replace(/\s+/g, '_').toLowerCase()}`]: v }))
  }

  createChart(config.canvasId, 'bar', {
    labels,
    datasets: [
      {
        type: 'bar',
        label: `${config.title} (Rp)`,
        data: barData,
        backgroundColor: '#60A5FA',
        yAxisID: 'y-bar',
        order: 2,
      },
      {
        type: 'line',
        label: `${config.title} (%)`,
        data: lineData,
        borderColor: '#F97316',
        backgroundColor: '#F97316',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 3,
        yAxisID: 'y-line',
        order: 1,
      },
    ],
  }, mergeChartOptions(
    {
      scales: {
        'y-bar': {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: { display: true, text: 'Value (Rp)' },
          ticks: { callback: (v: any) => shortenCurrency(v) }
        },
        'y-line': {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: { display: true, text: 'Percentage (%)' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v: any) => `${v.toFixed(1)}%` }
        }
      }
    },
    chartTooltip({ 
      label: (ctx: any) => {
        if (ctx.dataset.type === 'line') return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`;
        return `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`;
      }
    })
  ))
}

export function generateSpecificSubCategoryRatioChart(
  reports: any[],
  config: {
    canvasId: string
    mainCategory: string | string[]
    subCategory: string
    title: string
    alsoStore?: any
  }
) {
  const labels = reports.map((r) => {
    const d = new Date(r.period + '-01')
    return d.toLocaleString('default', { month: 'short', year: '2-digit' })
  })

  // [FIX] Calculate both Bar (Absolute) and Line (%) data
  const barData = reports.map((r) => {
    let subCategoryValue = 0;
    const categories = Array.isArray(config.mainCategory) ? config.mainCategory : [config.mainCategory];
    categories.forEach(cat => {
        if (r.pnlData && r.pnlData[cat] && r.pnlData[cat][config.subCategory]) {
            subCategoryValue += r.pnlData[cat][config.subCategory];
        }
    });
    return subCategoryValue;
  });

  const lineData = reports.map((r, i) => {
    const metrics = calculateAllPnlMetrics(r.pnlData || {})
    const revenue = metrics['Pendapatan (Revenue)'] || 0
    const subCategoryValue = barData[i];
    return revenue > 0 ? (subCategoryValue / revenue) * 100 : 0
  })

  if (config.alsoStore) {
    const aiData: Record<string, string> = {}
    labels.forEach((label, i) => {
      aiData[label] = `${formatDecimalBasedPercentage(lineData[i] / 100)}`
    })
    config.alsoStore(aiData, (v: any) => ({ [`ratio_${config.title.replace(/\s+/g, '_').toLowerCase()}`]: v }))
  }

  // [FIX] Use dual-axis chart (Bar + Line)
  createChart(config.canvasId, 'bar', {
    labels,
    datasets: [
      {
        type: 'bar',
        label: `${config.title} (Rp)`,
        data: barData,
        backgroundColor: '#3892FF', // Amber-300
        yAxisID: 'y-bar',
        order: 2,
      },
      {
        type: 'line',
        label: `${config.title} (%)`,
        data: lineData,
        borderColor: '#D97706', // Amber-600
        backgroundColor: '#D97706',
        borderWidth: 2,
        tension: 0.1,
        pointRadius: 3,
        yAxisID: 'y-line',
        order: 1,
      },
    ],
  }, mergeChartOptions(
    {
      scales: {
        'y-bar': {
          type: 'linear',
          position: 'left',
          beginAtZero: true,
          title: { display: true, text: 'Value (Rp)' },
          ticks: { callback: (v: any) => shortenCurrency(v) }
        },
        'y-line': {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: { display: true, text: '% of Revenue' },
          grid: { drawOnChartArea: false },
          ticks: { callback: (v: any) => `${v.toFixed(1)}%` }
        }
      }
    },
    chartTooltip({ 
      label: (ctx: any) => {
        if (ctx.dataset.type === 'line') return `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)}%`;
        return `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`;
      }
    })
  ))
}